import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import AddressGroupV2 from 'components/field/AddressGroupV2';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import { WAREHOUSES } from 'services/consts';

import { getPickupLocationCountries } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';

const OrderPickUpLocation = ({ nextStep, setWarehouseID }, ref) => {
	const allowedCountriesStr = useSelector((state) => state.user.localization.allowedCountries);
	const { t } = useTranslation();

	const { Country } = useLocale();

	const allowedCountries = useMemo(
		() => (allowedCountriesStr ? [Country, ...allowedCountriesStr.split(',')] : [Country]),
		[allowedCountriesStr]
	);
	const defaultColSpan = {
		md: 6
	};
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();

	const user = useSelector((state) => state.user);
	const senderName = user.fullName;
	const senderMobileNumber = user.phone;
	const senderEmail = user.email;
	const { planName = null } = useSelector((state) => state.user);

	const schema = useMemo(
		() =>
			yup.object().shape({
				// Pickup Location
				senderAddress: yup.object().shape({
					text: yup.string().nullable().required(),
					countryName: yup.string().isCountryAllowed().nullable().required(),
					stateName: yup.string().nullable(),
					cityName: yup.string().nullable().required(),
					districtName: yup.string().nullable(),
					neighbourhoodName: yup.string().nullable(),
					street: yup.string().nullable(),
					buildingName: yup.string().nullable(),
					zipCode: yup.string().nullable(),
					lat: yup.number().min(-90).max(90).nullable(),
					lng: yup.number().min(-180).max(180).nullable(),
					addressInputType: yup.string().nullable()
				}),
				senderEmail: yup.string().email().nullable().required(),
				senderMobileNumber: yup.string().nullable().phoneNumber().required(),
				senderName: yup.string().nullable().required(),
				warehouseName: yup.string().nullable().min(3).trim().required()
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			// Pickup Location
			senderAddress: {
				text: null,
				countryName: Country,
				stateName: null,
				cityName: null,
				districtName: null,
				neighbourhoodName: null,
				street: null,
				buildingName: null,
				zipCode: null,
				lat: null,
				lng: null
			},
			senderEmail,
			senderName,
			senderMobileNumber,
			warehouseName: t('myPickupLocation')
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isSubmitting }
	} = formMethods;

	const onSubmit = async (data) => {
		try {
			const response = await axios.post(WAREHOUSES.UPDATE_PICKUP_LOCATION, {
				address: { ...data.senderAddress },
				email: data.senderEmail,
				senderName: data.senderName,
				mobileNumber: data.senderMobileNumber,
				warehouseName: data.warehouseName
			});

			if (response.status === 200) {
				dispatch(getPickupLocationCountries({ invalidate: true }));
				response.data?.ID && setWarehouseID(response.data?.ID);
				nextStep();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			async save() {
				await handleSubmit(onSubmit, (errors) => {
					AppLogger.log(errors);
				})();
			}
		}),
		[handleSubmit, onSubmit]
	);

	return (
		<Form borderless formMethods={formMethods}>
			<FormItem
				colSpan={defaultColSpan}
				label="pickupLocationName"
				isLoading={isSubmitting}
				required
				toolTip="pickupLocation.pickupLocationNameTooltip">
				<StringInput name="warehouseName" />
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="senderName" isLoading={isSubmitting} required>
				<StringInput name="senderName" />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="email" isLoading={isSubmitting} required>
				<StringInput name="senderEmail" />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="mobileNumber" isLoading={isSubmitting} required>
				<PhoneNumber
					name="senderMobileNumber"
					value={watch('senderMobileNumber')}
					onChange={(value) =>
						setValue('senderMobileNumber', value, { shouldValidate: true, shouldDirty: true })
					}
				/>
			</FormItem>
			<AddressGroupV2
				formFieldName="senderAddress"
				isLoading={isSubmitting}
				restrictions={
					planName !== 'EnterprisePackage'
						? {
								country: {
									isNotDifferent: planName === 'FreePackage',
									code: planName === 'FreePackage' ? Country : allowedCountries
								}
							}
						: null
				}
			/>
		</Form>
	);
};

export default forwardRef(OrderPickUpLocation);
